import React, { useEffect, useState } from "react";
import {
  getPaises,
  postPais,
  putPais,
} from "../../componentes/http/actions/PaisAction";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import NuevoPaisModal from "../../componentes/modal/Pais/NuevoPaisModal";
import EditarPaisModal from "../../componentes/modal/Pais/EditarPaisModal";
import FilterComponent from "../../componentes/data/FilterComponent";

const GestorPais = () => {
  const [paises, setPaises] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [paisSeleccionado, setPaisSeleccionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHabilitado, setFilterHabilitado] = useState("todos");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchPaises();
  }, []);

  const fetchPaises = () => {
    setIsLoading(true);
    getPaises(localStorage.getItem("token"))
      .then((response) => {
        setPaises(response);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  };

  const filterOptions = [
    { value: "todos", label: "Todos" },
    { value: "habilitado", label: "Habilitados" },
    { value: "deshabilitado", label: "Deshabilitados" },
  ];

  const filteredPaises = paises
    .filter((pais) => {
      const matchesSearch = 
        pais.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pais.codigoISO.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterHabilitado === "todos" ||
        (filterHabilitado === "habilitado" && pais.habilitado === true) ||
        (filterHabilitado === "deshabilitado" && pais.habilitado === false);
      return matchesSearch && matchesFilter;
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(0);
  };
  
  const handleFilterChange = (newFilter) => {
    setFilterHabilitado(newFilter);
    setPage(0);
  };

  // Modal de nuevo país
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // Modal de editar país
  const handleOpenEditModal = (pais) => {
    setPaisSeleccionado(pais);
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setPaisSeleccionado(null);
  };

  const handleNuevoPais = async (nuevo) => {
    try {
      setIsLoading(true);
      await postPais(localStorage.getItem("token"), nuevo);
      await fetchPaises();
      handleCloseModal();
      setSnackbar({
        open: true,
        message: "País agregado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al agregar nuevo país:", error);
      setSnackbar({
        open: true,
        message: "Error al agregar país: el país ya existe o error en el servidor",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditarPais = async (editado) => {
    try {
      setIsLoading(true);
      await putPais(localStorage.getItem("token"), editado);
      await fetchPaises();
      handleCloseEditModal();
      setSnackbar({
        open: true,
        message: "País editado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al editar país:", error);
      setSnackbar({
        open: true,
        message: "Error al editar país: el país ya existe o error en el servidor",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderNoDataMessage = () => (
    <TableContainer component={Paper}>
      <Box p={3} sx={{ width: '100%' }}>
        <Typography variant="body1" color="text.secondary">
          No se encontraron países con los criterios de búsqueda actuales.
        </Typography>
      </Box>
    </TableContainer>
  );

  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <Grid item>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Países</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
            disabled={isLoading}
          >
            Añadir país
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        <FilterComponent
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filterValue={filterHabilitado}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
        />
      </Grid>
      <Grid item>
        {isLoading && !initialDataLoaded ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : filteredPaises.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Id</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Código ISO</TableCell>
                    <TableCell>Habilitado</TableCell>
                    <TableCell sx={{ display: "flex", justifyContent: "center" }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPaises.map((pais, index) => (
                    <TableRow key={index}>
                      <TableCell>{pais.id}</TableCell>
                      <TableCell>{pais.nombre}</TableCell>
                      <TableCell>{pais.codigoISO}</TableCell>
                      <TableCell>
                        <Chip
                          label={pais.habilitado ? "Sí" : "No"}
                          color={pais.habilitado ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                          <Tooltip title="Editar país">
                            <Button
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                              onClick={() => handleOpenEditModal(pais)}
                            >
                              <EditNoteIcon />
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={paises.filter((pais) => {
                const matchesSearch = 
                  pais.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pais.codigoISO.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesFilter =
                  filterHabilitado === "todos" ||
                  (filterHabilitado === "habilitado" && pais.habilitado === true) ||
                  (filterHabilitado === "deshabilitado" && pais.habilitado === false);
                return matchesSearch && matchesFilter;
              }).length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        ) : (
          renderNoDataMessage()
        )}
      </Grid>
      <NuevoPaisModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleNuevoPais}
      />
      <EditarPaisModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleEditarPais}
        pais={paisSeleccionado}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GestorPais;